import React, { useEffect, useMemo, useState } from 'react';

const AUTHORIZED_ROLES = ['Super Admin', 'Grant Manager'];
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const EMPTY_FORM = { title: '', maxScore: '', description: '' };

async function readResponsePayload(response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return { message: text || 'Unexpected server response.' };
}

function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

const SetCriteriaScore = () => {
  const token = localStorage.getItem('token');
  const payload = token ? decodeToken(token) : null;
  const roles = payload
    ? Array.isArray(payload.roles) ? payload.roles : [payload.role].filter(Boolean)
    : [];
  const isAuthorized = roles.some((role) => AUTHORIZED_ROLES.includes(role));

  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [selectedProgramMeta, setSelectedProgramMeta] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState(EMPTY_FORM);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingCriteria, setLoadingCriteria] = useState(false);

  useEffect(() => {
    if (!token) return;
    const fetchPrograms = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/programs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const body = await response.json();
        setPrograms(Array.isArray(body) ? body : []);
      } catch (fetchError) {
        console.error('Failed to fetch programs:', fetchError);
      }
    };

    fetchPrograms();
  }, [token]);

  useEffect(() => {
    if (!selectedProgramId || !token) {
      setSelectedProgramMeta(null);
      setCriteria([]);
      return;
    }

    const fetchCriteria = async () => {
      setLoadingCriteria(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/api/programs/${selectedProgramId}/criteria`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to load program criteria');
        const body = await readResponsePayload(response);
        setSelectedProgramMeta(body);
        setCriteria(Array.isArray(body.scoringCriteria) ? body.scoringCriteria : []);
      } catch (fetchError) {
        console.error(fetchError);
        setError('Failed to load program scoring criteria.');
        setCriteria([]);
      } finally {
        setLoadingCriteria(false);
      }
    };

    fetchCriteria();
  }, [selectedProgramId, token]);

  const totalMaxScore = useMemo(
    () => criteria.reduce((sum, criterion) => sum + Number(criterion.maxScore || 0), 0),
    [criteria]
  );

  const resetForm = () => setModalForm(EMPTY_FORM);

  const persistCriteria = async (nextCriteria, successMessage) => {
    const response = await fetch(`${API_BASE_URL}/api/programs/${selectedProgramId}/criteria`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        scoringCriteria: nextCriteria,
        linkedOpportunityId: selectedProgramMeta?.linkedOpportunityId || '',
        linkedOpportunityTitle: selectedProgramMeta?.linkedOpportunityTitle || '',
        linkedOpportunityNumber: selectedProgramMeta?.linkedOpportunityNumber || '',
      }),
    });

    const body = await readResponsePayload(response);
    if (!response.ok) {
      throw new Error(body.message || 'Failed to save criteria');
    }

    setCriteria(Array.isArray(body.program?.scoringCriteria) ? body.program.scoringCriteria : nextCriteria);
    setSelectedProgramMeta((current) => ({
      ...(current || {}),
      ...body.program,
      totalMaxScore: body.totalMaxScore,
    }));
    setMessage(successMessage);
    setTimeout(() => setMessage(''), 3000);
  };

  const openAddModal = () => {
    if (!selectedProgramId) {
      setError('Please select a program first.');
      return;
    }
    setModalForm(EMPTY_FORM);
    setModalError('');
    setEditingIndex(null);
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    resetForm();
    setModalError('');
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    setModalError('');

    if (!modalForm.title.trim()) {
      setModalError('Title is required.');
      return;
    }
    if (!modalForm.maxScore || Number(modalForm.maxScore) <= 0) {
      setModalError('Max score must be greater than 0.');
      return;
    }

    const duplicate = criteria.some(
      (criterion) => criterion.title.toLowerCase() === modalForm.title.trim().toLowerCase()
    );
    if (duplicate) {
      setModalError('A criterion with this title already exists.');
      return;
    }

    const nextCriteria = [
      ...criteria,
      {
        title: modalForm.title.trim(),
        maxScore: Number(modalForm.maxScore),
        description: modalForm.description.trim(),
        score: 0,
      },
    ];

    try {
      setSaving(true);
      await persistCriteria(nextCriteria, 'Criterion added.');
      closeAddModal();
    } catch (saveError) {
      console.error(saveError);
      setModalError(saveError.message || 'Failed to save program scoring criteria.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (index) => {
    const criterion = criteria[index];
    setModalForm({
      title: criterion.title || '',
      maxScore: criterion.maxScore || '',
      description: criterion.description || '',
    });
    setEditingIndex(index);
    setModalError('');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingIndex(null);
    resetForm();
    setModalError('');
  };

  const handleModalUpdate = async (event) => {
    event.preventDefault();
    setModalError('');

    if (!modalForm.title.trim()) {
      setModalError('Title is required.');
      return;
    }
    if (!modalForm.maxScore || Number(modalForm.maxScore) <= 0) {
      setModalError('Max score must be greater than 0.');
      return;
    }

    const duplicate = criteria.some(
      (criterion, index) =>
        criterion.title.toLowerCase() === modalForm.title.trim().toLowerCase() && index !== editingIndex
    );
    if (duplicate) {
      setModalError('A criterion with this title already exists.');
      return;
    }

    const nextCriteria = criteria.map((criterion, index) =>
      index === editingIndex
        ? {
            ...criterion,
            title: modalForm.title.trim(),
            maxScore: Number(modalForm.maxScore),
            description: modalForm.description.trim(),
          }
        : criterion
    );

    try {
      setSaving(true);
      await persistCriteria(nextCriteria, 'Criterion updated.');
      closeEditModal();
    } catch (saveError) {
      console.error(saveError);
      setModalError(saveError.message || 'Failed to save program scoring criteria.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const criterion = criteria[index];
    if (!window.confirm(`Delete the "${criterion.title}" criterion?`)) return;

    const nextCriteria = criteria.filter((_, currentIndex) => currentIndex !== index);

    try {
      setSaving(true);
      setError('');
      await persistCriteria(nextCriteria, 'Criterion deleted.');
    } catch (saveError) {
      console.error(saveError);
      setError('Failed to save program scoring criteria.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="review-page">
        <section className="application-content">
          <div className="form-card">
            <p className="section-error">You do not have permission to modify scoring criteria.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="review-page">
      <section className="application-content">
        <div className="form-card">
          <h2 className="section-heading">Select Program</h2>
          <p className="form-description">Scoring criteria are managed at the program level and apply to all applications in that program.</p>
          <label className="form-field">
            <span>Program</span>
            <select
              value={selectedProgramId}
              onChange={(event) => {
                setSelectedProgramId(event.target.value);
                resetForm();
              }}
            >
              <option value="">Select a program</option>
              {programs.map((program) => (
                <option key={program.id || program._id} value={program.id || program._id}>
                  {program.programCode} - {program.name}
                </option>
              ))}
            </select>
          </label>

          {selectedProgramMeta ? (
            <div className="table-meta" style={{ marginTop: '0.75rem' }}>
              {selectedProgramMeta.linkedOpportunityTitle ? (
                <>
                  Linked NOFO: <strong>{selectedProgramMeta.linkedOpportunityTitle}</strong>
                  {selectedProgramMeta.linkedOpportunityNumber ? ` (${selectedProgramMeta.linkedOpportunityNumber})` : ''}
                </>
              ) : (
                'No NOFO has been linked to this program yet.'
              )}
            </div>
          ) : null}
        </div>

        <div className="form-card">
          <div className="section-top">
            <div>
              <h2 className="section-heading">Program Criteria</h2>
              <p className="form-description">These criteria are shared by all applications in the selected program.</p>
            </div>
            <button
              className="submit-btn"
              type="button"
              onClick={openAddModal}
              disabled={!selectedProgramId || saving}
            >
              Add New Criteria
            </button>
          </div>

          {error && <div className="section-error">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          {loadingCriteria ? (
            <p className="no-results">Loading program criteria...</p>
          ) : criteria.length === 0 ? (
            <p className="no-results">No criteria configured yet.</p>
          ) : (
            <div className="grants-table-wrapper">
              <table className="grants-table">
                <thead>
                  <tr>
                    <th>Criterion</th>
                    <th>Description</th>
                    <th>Max Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {criteria.map((criterion, index) => (
                    <tr key={`${criterion.title}-${index}`}>
                      <td>{criterion.title}</td>
                      <td className="criteria-description-cell">{criterion.description || '—'}</td>
                      <td>{criterion.maxScore}</td>
                      <td>
                        <button type="button" className="link-btn" onClick={() => handleEdit(index)}>Edit</button>
                        {' | '}
                        <button type="button" className="link-btn" onClick={() => handleDelete(index)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  <tr className="criteria-total-row">
                    <td colSpan={2}>Total</td>
                    <td>{totalMaxScore}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

      </section>

      {addModalOpen && (
        <div className="programs-modal-overlay" onClick={closeAddModal}>
          <div
            className="programs-modal criteria-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="criteria-modal-header">
              <div>
                <h2 className="programs-modal-title criteria-modal-title">Add New Criteria</h2>
                <p className="criteria-modal-subtitle">
                  Add a scoring criterion that will apply to every application in this program.
                </p>
              </div>
              <button
                type="button"
                onClick={closeAddModal}
                className="criteria-modal-close"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="criteria-modal-form" onSubmit={handleAdd} noValidate>
              <div className="criteria-modal-scoreband">
                <div className="criteria-modal-scorecard">
                  <span className="criteria-modal-scorelabel">Current total</span>
                  <strong>{totalMaxScore}</strong>
                </div>
                <p className="criteria-modal-helper">Add a scoring weight for this criterion.</p>
              </div>
              <div className="criteria-modal-grid">
                <label className="form-field">
                  <span>Title</span>
                  <input
                    value={modalForm.title}
                    onChange={(event) => setModalForm((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder="e.g. Statement of Need"
                  />
                </label>
                <label className="form-field">
                  <span>Max Score</span>
                  <input
                    type="number"
                    min="0"
                    value={modalForm.maxScore}
                    onChange={(event) => setModalForm((prev) => ({ ...prev, maxScore: event.target.value }))}
                    placeholder="e.g. 12"
                  />
                </label>
              </div>
              <label className="form-field criteria-modal-field-full">
                <span>Description</span>
                <textarea
                  value={modalForm.description}
                  onChange={(event) => setModalForm((prev) => ({ ...prev, description: event.target.value }))}
                  rows={5}
                  placeholder="Add reviewer guidance for this criterion"
                />
              </label>
              {modalError && <div className="section-error">{modalError}</div>}
              <div className="programs-modal-actions">
                <button type="button" className="cancel-btn criteria-modal-action" onClick={closeAddModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn criteria-modal-action criteria-modal-action-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Add New Criteria'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editModalOpen && (
        <div className="programs-modal-overlay" onClick={closeEditModal}>
          <div
            className="programs-modal criteria-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="criteria-modal-header">
              <div>
                <h2 className="programs-modal-title criteria-modal-title">Edit Criterion</h2>
                <p className="criteria-modal-subtitle">
                  Update the title, scoring weight, and reviewer guidance for this criterion.
                </p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="criteria-modal-close"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form className="criteria-modal-form" onSubmit={handleModalUpdate} noValidate>
              <div className="criteria-modal-scoreband">
                <div className="criteria-modal-scorecard">
                  <span className="criteria-modal-scorelabel">Current total</span>
                  <strong>{totalMaxScore}</strong>
                </div>
                <p className="criteria-modal-helper">Update the criterion details and score weight.</p>
              </div>
              <div className="criteria-modal-grid">
                <label className="form-field">
                  <span>Title</span>
                  <input
                    value={modalForm.title}
                    onChange={(event) => setModalForm((prev) => ({ ...prev, title: event.target.value }))}
                    placeholder="e.g. Impact, Feasibility"
                  />
                </label>
                <label className="form-field">
                  <span>Max Score</span>
                  <input
                    type="number"
                    min="0"
                    value={modalForm.maxScore}
                    onChange={(event) => setModalForm((prev) => ({ ...prev, maxScore: event.target.value }))}
                    placeholder="e.g. 25"
                  />
                </label>
              </div>
              <label className="form-field criteria-modal-field-full">
                <span>Description</span>
                <textarea
                  value={modalForm.description}
                  onChange={(event) => setModalForm((prev) => ({ ...prev, description: event.target.value }))}
                  rows={5}
                  placeholder="Describe what this criterion evaluates"
                />
              </label>
              {modalError && <div className="section-error">{modalError}</div>}
              <div className="programs-modal-actions">
                <button type="button" className="cancel-btn criteria-modal-action" onClick={closeEditModal}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn criteria-modal-action criteria-modal-action-primary">
                  Update Criterion
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetCriteriaScore;
